/* eslint-disable react-hooks/exhaustive-deps */
import {useEffect, useRef, useState} from "react";
import {io} from "socket.io-client";
import {useSelector, useDispatch} from "react-redux";
import {showSnackbar, showTemplateSnackbar} from "../actions/snackbar";
import {useMessageHandler} from "./useMessageHandler";
import {useContactHandler} from "./useContactHandler";
import {updateContact, addContact} from "../actions/assignedContacts";
import {updateAgentPresence} from "../actions/agentes";
import {playNotificationSound} from "../notifications/notificationsPlayer";
import {buildAuthPayload} from "../utils/fingerprint"
import {isMessageDuplicate} from "../utils/messageDedup"
import {
    getNotificationPreferences,
    saveNotificationPreferences,
    shouldShowNotification,
    resolveInboxTone,
} from "../notifications/notificationSettings";
import {getSoundIdByUrl} from "../notifications/notificationSounds";
import {showIncomingDesktopNotification} from "../notifications/desktopNotifications";
import {typingSet, typingClear, typingPrune} from "../actions/typing";
import {focusSet, focusClear, focusPrune} from "../actions/focus";
import {trayQueueSyncContact, setActiveTray} from "../reducers/trays";
import {selectIdAgent} from "../actions/agentes";

export const useSocket = (path) => {

    const [socket, setSocket] = useState(null);
    const [online, setOnline] = useState(false);

    const dispatch = useDispatch();

    const agent = useSelector((state) => state.agent);
    const {id_customer, _id, email} = agent;

    const {contactsMessages, selectContactMessages} = useSelector(
        (state) => state.messages
    );

    const {contactsAssigneds} = useSelector(
        (state) => state.assignedContact
    );

    const {contactDetails, savedContactDetails} = useSelector(
        (state) => state.contact
    );

    const typingByContact = useSelector(
        (state) => state.typing?.typingByContact
    );

    const focusedByContact = useSelector(
        (state) => state.focus?.focusedByContact
    );

    const activeTrayId = useSelector((state) => state.trays?.activeTrayId);

    /* =========================
       🧠 Refs anti-closure
    ========================= */

    const refContactMessages = useRef(contactsMessages);
    const refAssignedAgentContacts = useRef(contactsAssigneds);
    const refSelectContactMessages = useRef(selectContactMessages);
    const refSavedContactDetails = useRef(savedContactDetails);
    const refContactDetails = useRef(contactDetails);
    const refIdMyAgent = useRef(_id);
    const refAgentNotificationSettings = useRef(agent?.notification_settings);
    const refActiveContactRoom = useRef(null);
    const refIsDuplicate = useRef(null);
    const refHandleMessageActions = useRef(null);
    const refHandleAgentMove = useRef(null);
    const refActiveTrayId = useRef(activeTrayId);

    const lastNotificationRef = useRef({
        key: null,
        at: 0,
    });

    /* =========================
       🧩 Handlers externos
    ========================= */

    const {handleMessageActions, isDuplicate} = useMessageHandler({
        refContactDetails,
        refSelectContactMessages,
        refContactMessages,
        refSavedContactDetails,
        refIdMyAgent,
    });

    const {handleAgentMove, updateContactReceived} = useContactHandler({
        refContactDetails,
        refAssignedAgentContacts,
        refIdMyAgent,
    });

    /* =========================
       🔄 Sync refs
    ========================= */

    useEffect(() => { refIdMyAgent.current = _id }, [_id]);
    useEffect(() => { refIsDuplicate.current = isDuplicate }, [isDuplicate]);
    useEffect(() => { refAgentNotificationSettings.current = agent?.notification_settings }, [agent?.notification_settings]);
    useEffect(() => { refHandleMessageActions.current = handleMessageActions }, [handleMessageActions]);
    useEffect(() => { refHandleAgentMove.current = handleAgentMove }, [handleAgentMove]);
    useEffect(() => { refSelectContactMessages.current = selectContactMessages }, [selectContactMessages]);
    useEffect(() => { refContactDetails.current = contactDetails }, [contactDetails]);
    useEffect(() => { refContactMessages.current = contactsMessages }, [contactsMessages]);
    useEffect(() => { refAssignedAgentContacts.current = contactsAssigneds }, [contactsAssigneds]);
    useEffect(() => { refSavedContactDetails.current = savedContactDetails }, [savedContactDetails]);
    useEffect(() => { refActiveTrayId.current = activeTrayId }, [activeTrayId]);

    /* =========================
       🔔 Notification settings
    ========================= */

    useEffect(() => {

        const personalInbox = agent?.notification_settings?.personal_inbox;

        if (!personalInbox) return;

        const current = getNotificationPreferences();
        const toneFromAgent = getSoundIdByUrl(personalInbox.tone);

        const enabledFromAgent =
            typeof personalInbox.enabled === "boolean"
                ? personalInbox.enabled
                : current.enabled;

        if (
            current.tone !== toneFromAgent ||
            current.enabled !== enabledFromAgent
        ) {
            saveNotificationPreferences({
                volume: current.volume,
                tone: toneFromAgent,
                enabled: enabledFromAgent,
            });
        }

    }, [
        agent?.notification_settings?.personal_inbox?.tone,
        agent?.notification_settings?.personal_inbox?.enabled
    ]);

    /* =========================
       🔌 Inicializar socket
    ========================= */

    useEffect(() => {

        if (!email || !id_customer || !path) return;

        let newSocket;

        const initSocket = async () => {

            const auth = await buildAuthPayload(email, id_customer);

            newSocket = io(path, {
                transports: ["websocket"],
                autoConnect: true,
                timeout: 20000,
                reconnection: true,
                reconnectionAttempts: Infinity,
                reconnectionDelay: 1000,
                reconnectionDelayMax: 10000,
                randomizationFactor: 0.5,
                auth,
            });

            setSocket(newSocket);
            newSocket.connect();

        };

        initSocket();

        return () => newSocket?.disconnect();

    }, [email, id_customer, path]);

    /* =========================
       🔌 Conexión
    ========================= */

    useEffect(() => {

        if (!socket) return;

        const onConnect = () => {
            console.log("🟢 [gateway] Socket conectado:", socket.id);
            setOnline(true);
        };

        const onDisconnect = () => {
            console.log("🔴 [gateway] Socket desconectado");
            setOnline(false);
        };

        const pingInterval = setInterval(() => {
            if (socket.connected) {
                socket.emit("presence:ping");
            }
        }, 30_000);

        socket.on("connect", onConnect);
        socket.on("disconnect", onDisconnect);

        return () => {
            socket.off("connect", onConnect);
            socket.off("disconnect", onDisconnect);
            clearInterval(pingInterval);
        };

    }, [socket]);

    useEffect(() => {
        return () => {
            socket?.disconnect();
        };
    }, [socket]);

    /* =========================
       🚪 Join / Leave contact room
    ========================= */

    useEffect(() => {

        if (!socket || !socket.connected) return;

        const newId = contactDetails?._id ?? null;
        const prevId = refActiveContactRoom.current;

        if (newId === prevId) return;

        refActiveContactRoom.current = newId;

        const timer = setTimeout(() => {

            if (prevId) {
                socket.emit("leave_room", `contact:${prevId}`, (ack) => {
                    if (ack?.success === false) {
                        console.error("❌ [gateway] leave_room falló:", `contact:${prevId}`, ack?.error);
                    } else {
                        console.log("👋 [gateway] left contact room:", `contact:${prevId}`);
                    }
                });
            }

            if (newId) {
                console.log("🚪 [gateway] emitting join_room:", `contact:${newId}`, `connected:${socket.connected}`);
                socket.emit("join_room", `contact:${newId}`, (ack) => {
                    if (ack?.success === false) {
                        console.error("❌ [gateway] join_room falló:", `contact:${newId}`, ack?.error);
                    } else {
                        console.log("✅ [gateway] joined contact room:", `contact:${newId}`);
                    }
                });
            }

        }, 0);

        return () => {
            clearTimeout(timer);
            refActiveContactRoom.current = prevId;
        };

    }, [contactDetails?._id, socket, online]);

    /* =========================
       📩 Mensajes nuevos
    ========================= */

    const isCurrentContactSelected = (message) => {

        if (!refContactDetails.current?._id) return false;

        return (
            String(refContactDetails.current._id) ===
            String(message.id_contact)
        );

    };

    const getNotificationKey = (message) => {

        const stableId = message?.id || message?._id;

        if (stableId) return String(stableId);

        return [
            message?.id_contact || "",
            message?.body || "",
            message?.date_create || "",
            String(message?.incoming),
        ].join("|");

    };

    useEffect(() => {

        if (!socket) return;

        const onNewMessage = (data) => {
            console.log("📨 [gateway] new_message recibido", data);
            const raw = data?.payload || data?.message || data;
            let message;
            if (typeof raw === 'string') {
                try { message = JSON.parse(raw); } catch { return; }
            } else {
                message = raw;
            }

            if (!message) return;

            if (message?.sync_template !== undefined) {
                dispatch(showTemplateSnackbar(message.sync_template));
            }

            if (message.incoming !== true && message.incoming !== false) return;

            if (isMessageDuplicate(message)) return;

            if (message.id_contact) {
                dispatch(typingClear({ id_contact: String(message.id_contact) }));
            }

            const isActiveContact = isCurrentContactSelected(message);
            console.log("🔍 [gateway] isActiveContact:", isActiveContact, "| contactDetails._id:", refContactDetails.current?._id, "| message.id_contact:", message.id_contact);

            if (
                message.incoming === true &&
                !refIsDuplicate.current(message) &&
                !isActiveContact
            ) {

                const notificationKey = getNotificationKey(message);
                const now = Date.now();

                const isDuplicatedNotification =
                    lastNotificationRef.current.key === notificationKey &&
                    now - lastNotificationRef.current.at < 2000;

                if (isDuplicatedNotification) return;

                lastNotificationRef.current = {
                    key: notificationKey,
                    at: now,
                };

                const localPrefs = getNotificationPreferences();

                const shouldNotify = shouldShowNotification(
                    refAgentNotificationSettings.current,
                    {
                        agentId: refIdMyAgent.current,
                        contactAgentId: message?.contact?.id_agent,
                    }
                );

                if (shouldNotify) {
                    // El sonido usa el tono del inbox del mensaje; null = silencio
                    const toneId = resolveInboxTone(
                        refAgentNotificationSettings.current,
                        {
                            agentId: refIdMyAgent.current,
                            contactAgentId: message?.contact?.id_agent,
                            fallbackTone: localPrefs.tone,
                        }
                    );

                    if (toneId) {
                        playNotificationSound(
                            toneId,
                            localPrefs.volume,
                            {
                                interrupt: false,
                                minIntervalMs: 1000,
                            }
                        ).catch(() => {});
                    }

                    dispatch(
                        showSnackbar({
                            agentName: message?.agent_inbox?.display_name || "BOT",
                            contactName: message.contact?.first_name || "Contacto",
                            contactPhone: message.contact?.phone,
                        })
                    );

                    showIncomingDesktopNotification({
                        contactName: message.contact?.first_name || "Contacto",
                        contactPhone: message.contact?.phone,
                        contactId: message.contact?._id || message.id_contact,
                        messageId: message._id || message.id,
                        body: message.body,
                    });
                }

            }

            const contact = message.contact;
            // Normalizar: el servidor omite id_agent cuando es null
            const contactIdAgent = contact?.id_agent !== undefined ? contact.id_agent : null;
            const normalizedContact = contact && contactIdAgent !== contact.id_agent
                ? { ...contact, id_agent: contactIdAgent }
                : contact;

            // id_tray (destino) y previous_tray (origen) viven en el nivel superior del
            // sobre (hermanos de `message`), no en message.contact — mergerlos ANTES de
            // usarlos para que contactDetails.id_tray no quede desactualizado al mover un
            // contacto entre bandejas (bug: 404 al quitarlo de la bandeja).
            const syncContact = normalizedContact ? { ...normalizedContact } : null;
            if (syncContact && data.id_tray !== undefined) syncContact.id_tray = data.id_tray;
            if (syncContact && data.previous_tray !== undefined) syncContact.previous_tray = data.previous_tray;

            if (isActiveContact) {
                // Tomado desde una bandeja: al confirmarse la asignación por socket,
                // salir de la bandeja y abrir mi inbox.
                if (refActiveTrayId.current && message.contact?.id_agent === refIdMyAgent.current) {
                    dispatch(setActiveTray(null));
                    dispatch(selectIdAgent(refIdMyAgent.current));
                }
                // syncContact lleva el id_tray autoritativo del sobre → contactDetails no queda stale.
                refHandleMessageActions.current?.(syncContact, message);
                refHandleAgentMove.current?.(
                    normalizedContact,
                    refAssignedAgentContacts.current
                );
            }

            if (contact) {
                dispatch(trayQueueSyncContact(syncContact));

                const agentList = refAssignedAgentContacts.current || [];
                const allContacts = agentList.flatMap(a => a.contacts);
                const existsInList = allContacts.some(c => c._id === normalizedContact._id);

                if (existsInList) {
                    dispatch(updateContact(normalizedContact));
                } else {
                    // Contacto nuevo — agregar si su agente es visible en cualquier bandeja
                    const isVisibleAgent = agentList.some(a => a._id === contactIdAgent);
                    if (isVisibleAgent) {
                        dispatch(addContact(normalizedContact));
                    }
                }
            }

        };

        socket.on("new_message", onNewMessage);

        return () => {
            socket.off("new_message", onNewMessage);
        };

    }, [socket]);

    /* =========================
       ✍️ Typing
    ========================= */

    useEffect(() => {

        if (!socket) return;

        const onTypingUpdate = (data) => {
            const user = data?.user;
            const id_contact = String(data?.room || "").replace(/^contact:/, "");
            if (!id_contact || !user?.email) return;
            if (user.email === email) return;

            const base = { id_contact, email: user.email };

            if (data.status === "start") {
                dispatch(typingSet({ ...base, displayName: user.display_name, lastAt: Date.now() }));
            } else if (data.status === "stop") {
                dispatch(typingClear(base));
            }
        };

        socket.on("typing:update", onTypingUpdate);

        return () => socket.off("typing:update", onTypingUpdate);

    }, [socket, email]);

    // Prune activo: elimina typing vencido (TTL 4s) aunque no llegue typing:stop
    useEffect(() => {
        if (!typingByContact || Object.keys(typingByContact).length === 0) return;
        const t = setInterval(() => dispatch(typingPrune()), 1000);
        return () => clearInterval(t);
    }, [typingByContact]);

    /* =========================
       🎯 Focus (contacto atendido)
    ========================= */

    useEffect(() => {
        if (!socket) return;

        const onFocusUpdate = (data) => {
            const user = data?.user;
            const id_contact = data?.contact_id;
            if (!id_contact || !user?.email) return;
            if (user.email === email) return;

            const base = { id_contact, email: user.email };

            if (data.status === "start") {
                dispatch(focusSet({ ...base, displayName: user.display_name, lastAt: Date.now() }));
            } else if (data.status === "stop") {
                dispatch(focusClear(base));
            }
        };

        socket.on("focus:update", onFocusUpdate);

        return () => socket.off("focus:update", onFocusUpdate);
    }, [socket, email]);

    // Prune activo: elimina focus vencido (TTL 10s) aunque no llegue focus:stop
    useEffect(() => {
        if (!focusedByContact || Object.keys(focusedByContact).length === 0) return;
        const t = setInterval(() => dispatch(focusPrune()), 1000);
        return () => clearInterval(t);
    }, [focusedByContact]);

    /* =========================
       🔄 Contact update
    ========================= */

    useEffect(() => {

        if (!socket) return;

        const onUpdateContact = (contact) => {
            const raw = contact?.payload || contact;
            const data = raw.id_agent !== undefined ? raw : { ...raw, id_agent: null };
            console.log("📬 [gateway] contact_updated recibido", { _id: data._id, id_agent: data.id_agent });
            dispatch(trayQueueSyncContact(data));
            updateContactReceived(data, refAssignedAgentContacts.current);
        };

        socket.on("contact_updated", onUpdateContact);
        socket.on("update_contact", onUpdateContact);

        return () => {
            socket.off("contact_updated", onUpdateContact);
            socket.off("update_contact", onUpdateContact);
        };

    }, [socket]);

    /* =========================
       🟢 Presencia
    ========================= */

    useEffect(() => {

        if (!socket) return;

        const onPresenceChanged = (data) => {
            const presence = data?.payload || data;
            if (!presence?.agent_email) return;
            dispatch(updateAgentPresence({
                agent_email: presence.agent_email,
                online: presence.online,
                last_seen: presence.last_seen,
            }));
        };

        socket.on("agent_presence_changed", onPresenceChanged);

        return () => socket.off("agent_presence_changed", onPresenceChanged);

    }, [socket]);

    /* =========================
       ❌ Errores
    ========================= */

    useEffect(() => {

        if (!socket) return;

        const onConnectError = (error) => {
            console.error("❌ [gateway] Socket connect_error:", error);
            if (error.message === "INSTANCE_CAPACITY_REACHED") {
                console.warn("⚠️ [gateway] Socket instance full, retrying...");
            }
        };

        const onError = (err) => {
            console.error("❌ [gateway] Socket error:", err);
        };

        socket.on("connect_error", onConnectError);
        socket.on("error", onError);

        return () => {
            socket.off("connect_error", onConnectError);
            socket.off("error", onError);
        };

    }, [socket]);

    return {
        socket,
        online,
    };
};
